/**
 * useForeignKeyData Hook
 *
 * Fetches related data from a referenced table for FK exploration.
 * Uses TanStack Query for caching.
 */

import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../services/tableExplorerApi';
import type { FilterGroup } from '../../core/types/workspaceTypes';
import { hostCacheIdentity, type TableExplorerRelationshipPort } from '../components/tableExplorerHostPorts';

type UseForeignKeyDataParams = {
  connectionId: string;
  database: string;
  referencedSchema: string;
  referencedTable: string;
  referencedColumn: string;
  value: unknown;
  enabled: boolean;
  targetFilter?: FilterGroup[];
  declarationId?: string;
  relationshipPort?: Pick<TableExplorerRelationshipPort, 'resolveRow' | 'cacheScope'>;
};

export type ForeignKeyRowData = {
  row: Record<string, unknown> | null;
  columns: Array<{ name: string; dataType: string }>;
  /** A host's presentation seam (the storage entity grid's date-times; last UI/UX pass, F14). SQL paths set none. */
  displayText?: (value: unknown, columnName: string) => string | undefined;
};

function normalizeFilterValue(value: unknown): string | number | boolean | Date | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value;
  return JSON.stringify(value);
}

/**
 * Hook to fetch a single related row from a referenced table
 */
export function useForeignKeyData(params: UseForeignKeyDataParams) {
  const {
    connectionId,
    database,
    referencedSchema,
    referencedTable,
    referencedColumn,
    value,
    enabled,
    targetFilter,
    declarationId,
    relationshipPort,
  } = params;

  return useQuery<ForeignKeyRowData>({
    // SQL keeps its original key byte for byte (S4 review C, N1). A host port
    // gets a JSON-safe, lossless identity under its own cache scope: a bigint
    // (Edm.Int64) value would make TanStack's key hashing throw (M2), and the
    // scope moves on reconnect and mutation so a stale point read is never
    // served (M5).
    queryKey: relationshipPort
      ? [
          ...(relationshipPort.cacheScope ?? ['host-relationship', connectionId]),
          'fk-data',
          database,
          referencedSchema,
          referencedTable,
          referencedColumn,
          hostCacheIdentity(value),
          hostCacheIdentity(targetFilter),
          declarationId ?? '',
        ]
      : [
          'fk-data',
          connectionId,
          database,
          referencedSchema,
          referencedTable,
          referencedColumn,
          value,
          targetFilter,
        ],
    queryFn: async () => {
      if (relationshipPort) {
        return relationshipPort.resolveRow({
          connectionId,
          database,
          referencedSchema,
          referencedTable,
          referencedColumn,
          value,
          targetFilter,
          declarationId,
        });
      }
      const filterValue = normalizeFilterValue(value);

      const executionId = createExecutionId(`fk-lookup-${referencedTable}`);
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId,
        table: { database, schema: referencedSchema, tableName: referencedTable },
        columns: '*',
        limit: 1,
        filters: [
          {
            field: referencedColumn,
            conditions: [{ operator: '=', value: filterValue }],
          },
          ...(targetFilter ?? []),
        ],
        sorting: [],
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch related data');
      }

      const columns = result.columns?.map((c) => ({ name: c.name, dataType: c.dataType })) || [];
      const rows = result.rows || [];

      if (rows.length === 0) {
        return {
          row: null,
          columns,
        };
      }

      return {
        row: rows[0],
        columns,
      };
    },
    enabled: enabled && !!connectionId && !!referencedTable && value !== null && value !== undefined,
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  });
}
